import { useEffect, useState } from 'react';
import { Notification, Stack } from '@mantine/core';
import { dismissNotice, subscribeNotices, type Notice } from '../lib/notify';

/**
 * A2-6: renders the global notice stack (see {@link ../lib/notify}). We use
 * `@mantine/core`'s `Notification` — the project has no `@mantine/notifications`
 * dependency and A2-6 forbids adding one. Fixed top-right, auto-dismissing.
 */
const AUTO_CLOSE_MS = 6000;
const COLOR: Record<Notice['level'], string> = { error: 'red', success: 'green', info: 'blue' };

function NoticeCard({ notice }: { notice: Notice }) {
  useEffect(() => {
    const timer = setTimeout(() => dismissNotice(notice.id), AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [notice.id]);

  return (
    <Notification
      color={COLOR[notice.level]}
      title={notice.title}
      onClose={() => dismissNotice(notice.id)}
      data-testid={`notice-${notice.level}`}
      withBorder
    >
      {notice.message}
    </Notification>
  );
}

export function NotificationHost() {
  const [notices, setNotices] = useState<readonly Notice[]>([]);
  useEffect(() => subscribeNotices(setNotices), []);

  if (notices.length === 0) return null;

  return (
    <Stack
      gap="sm"
      style={{
        position: 'fixed',
        top: 'var(--wb-space-lg, 16px)',
        right: 'var(--wb-space-lg, 16px)',
        zIndex: 1000,
        width: 380,
        maxWidth: 'calc(100vw - 32px)',
      }}
      data-testid="notification-host"
    >
      {notices.map((notice) => (
        <NoticeCard key={notice.id} notice={notice} />
      ))}
    </Stack>
  );
}
